/**
 * @fileoverview W3C Trace Context header formatting and parsing
 * @module headers
 */

/**
 * Format a traceparent header value
 * @param {string} traceId - 32-character hex trace ID
 * @param {string} spanId - 16-character hex span ID
 * @param {boolean} [sampled=true] - Whether the trace is sampled
 * @returns {string} Traceparent header value
 */
export function formatTraceparent(traceId, spanId, sampled = true) {
  const flags = sampled ? '01' : '00';
  return `00-${traceId}-${spanId}-${flags}`;
}

/**
 * Parse a traceparent header value
 * @param {string} traceparent - Traceparent header value
 * @returns {Object|null} Parsed values or null if invalid
 */
export function parseTraceparent(traceparent) {
  if (!traceparent || typeof traceparent !== 'string') {
    return null;
  }

  const parts = traceparent.split('-');
  if (parts.length !== 4) {
    return null;
  }

  const [version, traceId, spanId, flags] = parts;

  // Validate W3C Trace Context version (must be 00)
  // eslint-disable-next-line web/no-legacy-support -- This is W3C Trace Context protocol version, not software version
  if (version !== '00') {
    return null;
  }

  // Validate traceId (32 hex characters)
  if (!/^[0-9a-f]{32}$/i.test(traceId)) {
    return null;
  }

  // Validate spanId (16 hex characters)
  if (!/^[0-9a-f]{16}$/i.test(spanId)) {
    return null;
  }

  // Validate flags (2 hex characters)
  if (!/^[0-9a-f]{2}$/i.test(flags)) {
    return null;
  }

  return {
    version,
    traceId: traceId.toLowerCase(),
    spanId: spanId.toLowerCase(),
    sampled: (parseInt(flags, 16) & 0x01) === 0x01,
  };
}
